import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Paper,
  TextField,
} from '@material-ui/core';
import { CloudDownload, DeleteForever, Image, Unarchive, Visibility, VisibilityOff } from '@material-ui/icons';
import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirestore } from 'react-redux-firebase';
import { useHistory } from 'react-router-dom';
import { Doc, useAuthentication, useMedia } from '../../../hooks';
import { RootState } from '../../../store/reducers';
import Zip from 'jszip';
import { saveAs } from 'file-saver';

const AdminMenu: React.FC<{ selectedDocs?: Doc[]; onSearch?: (searchText: string) => void }> = ({
  selectedDocs = [],
  onSearch,
}) => {
  const history = useHistory();
  const [open, toggleOpen] = useState(false);
  const { deleteMedias, updateMedias, updateCategory } = useMedia();
  const categories = useSelector((state: RootState) => state.firestore?.ordered?.categories ?? []);
  const firestore = useFirestore();
  const [downloading, setDownloading] = useState(false);

  const {
    profile: { email },
  } = useAuthentication();

  const handleDeleteFiles = useCallback(() => {
    selectedDocs && deleteMedias({ selectedDocs });
    toggleOpen(false);
  }, [deleteMedias, selectedDocs]);

  const handleToggleHidden = useCallback(
    (hidden) =>
      selectedDocs &&
      updateMedias({
        selectedDocs: selectedDocs.map((doc) => ({ ...doc, hidden })),
      }),
    [updateMedias, selectedDocs]
  );

  const handleDownload = useCallback(
    async (doc: Doc) => {
      await firestore.collection('logs').add({
        doc: doc.metadata.name,
        email,
        action: 'download',
        date: dayjs().format('DD/MM/YYYY HH:mm:ss'),
        category: categories?.find((cat) => cat.id === doc.category)?.label ?? '',
      });
      const url = doc.url;
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function (res) {
          resolve(xhr.response);
        };
        xhr.open('GET', url);
        xhr.send();
      });
    },
    [categories, email, firestore]
  );

  const handleBulkDownload = useCallback(async () => {
    setDownloading(true);
    const zip = new Zip();
    let filename = 'files.zip';
    for (let doc of selectedDocs) {
      const blob = await handleDownload(doc);
      if (categories?.find((cat) => cat.id === doc.category)?.label) {
        filename = categories?.find((cat) => cat.id === doc.category)?.label + '.zip';
      }
      zip.file(doc.metadata.name, blob as any, { base64: true });
    }
    zip.generateAsync({ type: 'blob' }).then(function (content) {
      saveAs(content, filename);
    });
    setDownloading(false);
  }, [categories, handleDownload, selectedDocs]);

  const handleSetCatThumb = useCallback(
    () => updateCategory({ id: selectedDocs[0].category, thumb: selectedDocs[0].url }),
    [selectedDocs, updateCategory]
  );

  const isImage = (doc: Doc) => doc && doc.metadata.contentType.indexOf('image/') === 0;

  return (
    <Paper variant="outlined">
      <Dialog open={open} onClose={() => toggleOpen(false)}>
        <DialogTitle>Attenzione!</DialogTitle>
        <Box padding={3} paddingTop={0}>
          Sei sicuro di voler eliminare i file selezionati?
          <br />
          L'azione è irreversibile.
        </Box>
        <DialogActions>
          <Button onClick={() => toggleOpen(false)}>Annulla</Button>
          <Button onClick={handleDeleteFiles} variant="contained" color="primary" disableElevation>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
      <Box padding={2}>
        <small>Admin menu</small>
        <Divider style={{ margin: 10 }} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <TextField
            label="File"
            variant="outlined"
            size="small"
            onChange={(e) => onSearch && onSearch(e.target.value)}
          />
          <Box>
            {selectedDocs.length === 1 && (
              <Button
                style={{ marginRight: 5 }}
                variant="contained"
                color="primary"
                disableElevation
                disabled={!isImage(selectedDocs[0])}
                onClick={handleSetCatThumb}
              >
                <Image style={{ marginRight: 5 }} /> Thumb cat.
              </Button>
            )}
            {selectedDocs.length > 1 && (
              <Button
                style={{ marginRight: 5 }}
                variant="contained"
                color="primary"
                disableElevation
                onClick={handleBulkDownload}
                disabled={downloading}
              >
                {downloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    <CloudDownload style={{ marginRight: 5 }} /> Download
                  </>
                )}
              </Button>
            )}
            {!(selectedDocs.some((doc) => doc.hidden) && selectedDocs.some((doc) => !doc.hidden)) && (
              <Button
                style={{ marginRight: 5 }}
                variant="contained"
                color="primary"
                disableElevation
                onClick={() => handleToggleHidden(!selectedDocs.some((doc) => doc.hidden))}
                disabled={!selectedDocs?.length}
              >
                {selectedDocs.some((doc) => doc.hidden) ? (
                  <>
                    <Visibility style={{ marginRight: 5 }} /> Mostra
                  </>
                ) : (
                  <>
                    <VisibilityOff style={{ marginRight: 5 }} /> Nascondi
                  </>
                )}
              </Button>
            )}
            <Button
              style={{ marginRight: 5 }}
              variant="contained"
              color="primary"
              disableElevation
              disabled={!selectedDocs?.length}
              onClick={() => toggleOpen(true)}
            >
              <DeleteForever style={{ marginRight: 5 }} /> Elimina
            </Button>
            <Button
              style={{ marginRight: 5 }}
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => history.push('/upload')}
            >
              <Unarchive style={{ marginRight: 5 }} /> Carica file
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
export default AdminMenu;
